import React from 'react';
import ReactDOM from 'react-dom';
import Relay from 'react-relay';
import deffered from 'yaDeferred';
import * as constants from 'dibs-auth-modal/dist/constants';
import User from '../global/models/User';
import networkLayer from '../global/relayClientNetworkLayer.es';
import 'dibs-auth-modal/dist/main.css';

const relayEnvironment = new Relay.Environment();
relayEnvironment.injectNetworkLayer(networkLayer());

let Auth;
let wrapperElement;

function getWrapperElement() {
    if (!wrapperElement) {
        wrapperElement = document.createElement('div');
        wrapperElement.classList.add('authModalContainer');
        document.body.appendChild(wrapperElement);
    }
    return wrapperElement;
};

let authModalLoader = {
    _onLoaded: deffered(),
    /**
     * Splits the AuthModal to a separate chunk during build time. Loads the authModal when called.
     * For now there's no way to handle errors. Should be possible when we switch to webpack 2.
     * This is mostly an espace hatch if you need direct access to the authModal instance.
     *
     * @example
     * authModalLoader.load().then(AuthModal => DO STUFF)
     *
     * @returns {Promise} - A promise that resolves with the AuthModal module.
     */
    load() {
        require.ensure('dibs-auth-modal', () => {
            Auth = require('dibs-auth-modal');
            authModalLoader._onLoaded.resolve(Auth);
        }, 'AuthModal');

        return authModalLoader._onLoaded;
    },

    /**
     * Usefull when you don't want to load the AuthModal but want to get the instance.
     *
     * @example
     * authModalLoader.getLoadPromise().then(AuthModal => DO STUFF)
     *
     * @returns {Promise} - A promise that resolves with the AuthModal module.
     */
    getLoadPromise() {
        return authModalLoader._onLoaded;
    },

    /**
     * Shortcut to AuthModal.show.
     *
     * @example
     * authModalLoader.show(options).then((authResponse) => if (authResponse.err) FAILURE else SUCCESS);
     * authResponse.user will be an object containing user data: user.userId, user.userToken, user.profile {}.
     * authResponse.err will be an object with shape {err: 'closed'}.
     * it will contain the reason for authentication failure. which is most likely because the user closed the modal.
     * @returns {Promise}
     */
    show(options = {}) {
        const environment = options.relayEnvironment || relayEnvironment;
        return new Promise((resolve) => {
            this.load().then((AuthModal) => {
                ReactDOM.render(<AuthModal.AuthModal environment={environment} />, getWrapperElement());
                switch (options.action) {
                    case constants.LOGIN:
                        Auth.launchLogin({...options, resolve});
                        break;
                    case constants.RESET_PASSWORD:
                        Auth.launchResetPassword({...options, resolve});
                        break;
                    case constants.UPDATE_PASSWORD:
                        Auth.launchUpdatePassword({...options, resolve});
                        break;
                    default:
                        Auth.launchRegistration({...options, resolve});
                        break;
                }
            });
        }).then((authResult) => {
            const isRegister = authResult.modal === constants.REGISTER;
            const isLogin = authResult.modal === constants.LOGIN;

            if (!authResult.err) {
                User.updateLoginState();
                const {postLogin, postRegistration} = require('../postAuth/postAuthLoader.es');

                if (isLogin) {
                    postLogin({flow: options.flow, environment});
                } else if (isRegister) {
                    postRegistration({flow: options.flow, environment});
                }
            }
            return {...authResult, newUser: isRegister};
        });
    },

    getLoadPromise() {
        return authModalLoader._onLoaded;
    },

    /**
     * Used for testing only.
     */
    _clearAuthModal() {
        Auth = undefined;
        authModalLoader._onLoaded = deffered();
    },

    /**
     * Used for testing only.
     */
    _setAuthModal(m) {
        Auth = m;
    }
};

authModalLoader = Object.assign(authModalLoader, {constants});

export default authModalLoader;



// WEBPACK FOOTER //
// ./assets/js/auth/authModalLoader.jsx